import { useCallback, useEffect, useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { type DraftAction, type WizardDraft } from '../domain/wizardDraft'

export const AUTOSAVE_DEBOUNCE_MS = 400

const AUTOSAVED_ACTIONS: ReadonlySet<DraftAction['type']> = new Set([
  'itemEdited',
  'itemConfirmed',
  'valuesFilled',
  'custodianDetailsFetched',
  'stepChangesDiscarded',
  'intakeApplied',
])

export const shouldAutosave = (action: DraftAction, next: WizardDraft, submitted: boolean): boolean =>
  AUTOSAVED_ACTIONS.has(action.type) && !!next.openingId && !next.readOnly && !submitted

interface DraftAutosaveOptions {
  getDraft: () => WizardDraft
  persist: (draft: WizardDraft) => Promise<void>
  saveDetached: (draft: WizardDraft) => Promise<unknown>
}

interface DraftAutosave {
  schedule: () => void
  run: (save: () => Promise<void>) => Promise<void>
  flush: () => void
}

export const useDraftAutosave = (options: DraftAutosaveOptions): DraftAutosave => {
  const latest = useRef(options)
  useEffect(() => {
    latest.current = options
  })

  const queue = useRef<Promise<unknown>>(Promise.resolve())

  const enqueue = useCallback((save: () => Promise<unknown>): Promise<void> => {
    const next = queue.current.then(save).catch(() => undefined)
    queue.current = next
    return next.then(() => undefined)
  }, [])

  const debouncedSave = useDebouncedCallback(
    () => {
      void enqueue(() => latest.current.persist(latest.current.getDraft()))
    },
    AUTOSAVE_DEBOUNCE_MS,
    { flushOnExit: true },
  )

  const schedule = useCallback(() => debouncedSave(), [debouncedSave])

  const run = useCallback(
    (save: () => Promise<void>) => {
      debouncedSave.cancel()
      return enqueue(save)
    },
    [debouncedSave, enqueue],
  )

  const flush = useCallback(() => {
    if (!debouncedSave.isPending()) {
      return
    }
    debouncedSave.cancel()
    const draft = latest.current.getDraft()
    void enqueue(() => latest.current.saveDetached(draft))
  }, [debouncedSave, enqueue])

  return { schedule, run, flush }
}
